import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import "./StudentProgressTrend.css";
import DropDown from "../DummyReportPage/Dropdown";
import DropDownMultipleSelection2 from "../components/FieldsComponent";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import { fetchCourseData } from "../../../Redux/StudentRegistration/courseSlice";
import { FULL_API_BASE_URL } from "../../../Config/Config";
import TableTestReportCard from "../TestReportCard/TableTestReportCard";
import ErrorImg from "./../../../Assets/Images/CommanImages/Error.png";

const StudentProgressTrend = ({
  setMessageBox,
  setIsMessageBoxVisible,
  setActiveImg,
  dataClient,
  setPagePath,
  setNoTimeSet,
}) => {
  const dispatch = useDispatch();
  const { coursesdata } = useSelector((state) => state.course);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportName = searchParams.get("reportName");

  // ── dropdown state ──────────────────────────────────────────────────────────
  const [sessionData, setSessionData] = useState([]);
  const [batchData, setBatchData] = useState([]);
  const [testData, setTestData] = useState([]); // options for multi-select

  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedSessionId, setSelectedSessionId] = useState("");
  const [selectedBatchId, setSelectedBatchId] = useState("");
  const [selectedTestIds, setSelectedTestIds] = useState([]); // multi-select
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [resetKey, setResetKey] = useState(0);
  const [isApplied, setIsApplied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ── table state ─────────────────────────────────────────────────────────────
  const [headers, setHeaders] = useState([]);
  const [resultsData, setResultsData] = useState([]);
  const [isColumnDivVisible, setIsColumnDivVisible] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);

  // ── page path ────────────────────────────────────────────────────────────────
  useEffect(() => {
    setPagePath("Reports > Reports Dashboard > Student Progress Trend");
    dispatch(fetchCourseData());
    fetchSessionsData();
  }, [dispatch]);

  // ── sessions ─────────────────────────────────────────────────────────────────
  const fetchSessionsData = async () => {
    try {
      const res = await fetch(`${FULL_API_BASE_URL}/get-sessions-list`);
      const result = await res.json();
      if (result.status === 200 && result.data) {
        setSessionData(
          result.data.map((s) => ({ id: s.session_id, label: s.session_name }))
        );
      }
    } catch (err) {
      console.error("Error fetching sessions:", err);
    }
  };

  // ── batches (triggered by course) ───────────────────────────────────────────
  useEffect(() => {
    if (!selectedCourseId) return;
    const fetchBatches = async () => {
      try {
        const res = await fetch(
          `${FULL_API_BASE_URL}/get-batches-by-course/?course_id=${selectedCourseId}`
        );
        const data = await res.json();
        setBatchData(data || []);
      } catch (err) {
        Swal.fire({
          title: "Error!",
          text: err.message,
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    };
    fetchBatches();
  }, [selectedCourseId]);

  // ── tests (triggered by batch OR date range change) ──────────────────────────
  // Fetch the available tests for the multi-select whenever batch or dates change
  useEffect(() => {
    if (!selectedBatchId) {
      setTestData([]);
      setSelectedTestIds([]);
      return;
    }
    fetchTestsByBatch();
  }, [selectedBatchId, fromDate, toDate]);

  const fetchTestsByBatch = async () => {
    try {
      let url = `${FULL_API_BASE_URL}/get-tests-by-batch/?batch_id=${selectedBatchId}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;
      const res = await fetch(url);
      const result = await res.json();
      if (result.status === 200 && result.data) {
        setTestData(
          result.data.map((t) => ({
            id: t.test_schedule_id,
            label: t.test_name,
          }))
        );
        // Clear previously selected tests that are no longer in range
        setSelectedTestIds([]);
      } else {
        setTestData([]);
        setSelectedTestIds([]);
      }
    } catch (err) {
      console.error("Error fetching tests:", err);
    }
  };

  // ── validation: at least one of (date range field, test selection) must exist ─
  const isFilterValid = () => {
    const hasDateFilter = fromDate || toDate;
    const hasTestFilter = selectedTestIds.length > 0;
    return hasDateFilter || hasTestFilter;
  };

  // ── fetch report data ─────────────────────────────────────────────────────────
  const fetchResultsData = async () => {
    if (!selectedBatchId) {
      showError("Please select a Batch.");
      return;
    }
    if (!isFilterValid()) {
      showError("Please select a date range or at least one test.");
      return;
    }

    setNoTimeSet(true);
    setIsLoading(true);
    setIsMessageBoxVisible(true);
    setMessageBox(
      <div className="Container-messageBOX">
        <div className="messagebox-insider1">
          <div className="loader-tc"></div>
          <span style={{ marginLeft: "10px", marginTop: "3px" }}>
            Loading data, please wait...
          </span>
        </div>
      </div>
    );
    setActiveImg(false);

    const slowTimer = setTimeout(() => {
      setMessageBox(
        <div className="Container-messageBOX">
          <div className="messagebox-insider1">
            <div className="loader-tc"></div>
            <span style={{ marginLeft: "10px", marginTop: "3px" }}>
              This is taking longer than usual, please wait a moment...
            </span>
          </div>
        </div>
      );
    }, 10000);

    try {
      let url = `${FULL_API_BASE_URL}/student-progress-trend/?batch_id=${selectedBatchId}`;
      if (selectedTestIds.length > 0)
        url += `&test_schedule_ids=${selectedTestIds.join(",")}`;
      if (fromDate) url += `&from_date=${fromDate}`;
      if (toDate) url += `&to_date=${toDate}`;

      const res = await fetch(url);
      const data = await res.json();

      clearTimeout(slowTimer);

      if (
        data.status === 200 &&
        Array.isArray(data.data) &&
        data.data.length > 0
      ) {
        // Convert flat-object rows into [headers, ...rows] array-of-arrays format
        // that TableTestReportCard expects
        const cols = data.columns || Object.keys(data.data[0]);
        // Pretty-print column headers
        const prettyHeaders = cols.map((c) =>
          c.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
        );
        const rows = data.data.map((row) => cols.map((c) => row[c] ?? "N/A"));

        setHeaders(prettyHeaders);
        setResultsData(rows);
        setIsLoading(false);
        setNoTimeSet(false);
        setIsMessageBoxVisible(false);
      } else if (data.status === 200) {
        showMessage("No data found for the selected filters.");
      } else {
        showError(data.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      clearTimeout(slowTimer);
      showError(err.message);
    }
  };

  const showError = (msg) => {
    setIsLoading(false);
    setNoTimeSet(false);
    setIsMessageBoxVisible(true);
    setMessageBox(
      <div className="Container-messageBOX">
        <div className="messagebox-insider1">
          <span style={{ marginLeft: "10px", marginTop: "3px" }}>{msg}</span>
        </div>
      </div>
    );
    setActiveImg(ErrorImg);
    setIsApplied(false);
  };

  const showMessage = (msg) => {
    setIsLoading(false);
    setNoTimeSet(false);
    setIsMessageBoxVisible(true);
    setMessageBox(
      <div className="Container-messageBOX">
        <div className="messagebox-insider1">
          <span style={{ marginLeft: "10px", marginTop: "3px" }}>{msg}</span>
        </div>
      </div>
    );
    setActiveImg(false);
  };

  // ── apply / reset ─────────────────────────────────────────────────────────────
  const handleApplyOrReset = () => {
    if (isLoading) return;
    if (isApplied) {
      setIsApplied(false);
      setIsLoading(false);
      setHeaders([]);
      setResultsData([]);
      setIsColumnDivVisible(false);
      setIsMessageBoxVisible(false);
      setNoTimeSet(false);
    } else {
      setIsApplied(true);
      fetchResultsData();
    }
  };

  // ── reset all filters ─────────────────────────────────────────────────────────
  const resetAllFilters = () => {
    setSelectedCourseId("");
    setSelectedSessionId("");
    setSelectedBatchId("");
    setSelectedTestIds([]);
    setFromDate("");
    setToDate("");
    setBatchData([]);
    setTestData([]);
    setResetKey((k) => k + 1);
  };

  const dropdownStyle = isApplied
    ? { opacity: 0.5, pointerEvents: "none" }
    : { opacity: 1, pointerEvents: "auto" };

  const filteredBatchData = batchData.filter(
    (b) => !selectedSessionId || b.session_id === selectedSessionId
  );

  // Table expects data as array-of-arrays (each row = array indexed by header position)
  const tableData = resultsData;

  // Determine batch name for print header
  const batchName =
    batchData.find((b) => b.batch_id === selectedBatchId)?.batch_name || "";
  const courseName =
    coursesdata?.find((c) => c.course_id === selectedCourseId)
      ?.course_description || "";

  return (
    <div
      style={{
        height: "auto",
        width: "100%",
        paddingLeft: "36.5px",
        marginBottom: "40px",
      }}
    >
      <div style={{ position: "sticky", top: "0" }}>
        {/* ── Header ─────────────────────────────────────────────────────────── */}
        <div className="Header-up">
          <span
            style={{
              color: "#2F2E40",
              fontSize: "30px",
              textTransform: "capitalize",
            }}
          >
            {reportName || "Student Progress Trend"}
          </span>
          <div
            className="testreportcard-header-btn-container"
            style={{ marginRight: "30px" }}
          >
            <button
              className="testreportcard-cancel-btn"
              onClick={() => navigate("/report_management")}
            >
              CANCEL
            </button>
            <button
              className="testreportcard-create-btn"
              onClick={handleApplyOrReset}
              style={{
                opacity: isLoading ? 0.6 : 1,
                cursor: isLoading ? "not-allowed" : "pointer",
              }}
            >
              {isApplied ? "RESET" : "APPLY"}
            </button>
          </div>
        </div>

        <p style={{ marginTop: "1%", color: "#2F2E40" }}>
          View average subject-wise marks per student across selected tests or
          date range.
        </p>

        {/* ── Filters ────────────────────────────────────────────────────────── */}
        <div
          style={{ display: isVisible ? "block" : "none", marginTop: "1vh" }}
        >
          {/* Row 1: Course | Session | Batch */}
          <div className="row-cs-uni">
            <div
              className="col-cs2"
              style={{ marginRight: "1vw", ...dropdownStyle }}
              key={`course_${resetKey}`}
            >
              <DropDown
                label="Course"
                selected={selectedCourseId}
                setSelected={(val) => {
                  setSelectedCourseId(val);
                  setSelectedSessionId("");
                  setSelectedBatchId("");
                  setSelectedTestIds([]);
                  setBatchData([]);
                  setTestData([]);
                  setResetKey((k) => k + 1);
                }}
                options={
                  coursesdata?.map((c) => ({
                    label: c.course_description,
                    id: c.course_id,
                  })) || []
                }
                isRequired="true"
              />
            </div>

            <div
              className="col-cs2"
              style={{ marginRight: "1vw", ...dropdownStyle }}
              key={`session_${resetKey}`}
            >
              <DropDown
                label="Session"
                selected={selectedSessionId}
                setSelected={(val) => {
                  setSelectedSessionId(val);
                  setSelectedBatchId("");
                  setSelectedTestIds([]);
                  setTestData([]);
                  setResetKey((k) => k + 1);
                }}
                options={sessionData}
                isRequired="true"
              />
            </div>

            <div
              className="col-cs2"
              style={{ marginRight: "1vw", ...dropdownStyle }}
              key={`batch_${resetKey}`}
            >
              <DropDown
                label="Batch"
                selected={selectedBatchId}
                setSelected={(val) => {
                  setSelectedBatchId(val);
                  setSelectedTestIds([]);
                  setTestData([]);
                }}
                options={filteredBatchData.map((b) => ({
                  label: b.batch_name,
                  id: b.batch_id,
                }))}
                isRequired="true"
              />
            </div>
          </div>

          {/* Row 2: From Date | To Date | Test (multi-select) */}
          <div
            className="row-cs-uni"
            style={{ marginTop: "12px", alignItems: "flex-end" }}
          >
            {/* From Date */}
            <div
              className="col-cs2"
              style={{ marginRight: "1vw", ...dropdownStyle }}
            >
              <label className="spt-date-label">From Date</label>
              <input
                type="date"
                className="spt-date-input"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  // Clear test selection when date changes — will refetch
                  setSelectedTestIds([]);
                }}
              />
            </div>

            {/* To Date */}
            <div
              className="col-cs2"
              style={{ marginRight: "1vw", ...dropdownStyle }}
            >
              <label className="spt-date-label">To Date</label>
              <input
                type="date"
                className="spt-date-input"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setSelectedTestIds([]);
                }}
              />
            </div>

            {/* Test multi-select */}
            <div
              className="col-cs2"
              style={{ marginRight: "1vw", ...dropdownStyle }}
              key={`test_${resetKey}`}
            >
              <DropDownMultipleSelection2
                label="Tests"
                selected={selectedTestIds}
                setSelected={setSelectedTestIds}
                options={testData}
                showSelectAll={true}
                // isRequired="true"
                style={{ width: "100%" }}
              />
            </div>
          </div>

          {/* Validation hint */}
          {/* {selectedBatchId && !isFilterValid() && (
            <p className="spt-hint">
              * Select a date range, at least one test, or both to apply filters.
            </p>
          )} */}
        </div>

        {/* ── Three-dot toggle (same as TestReportCard) ────────────────────── */}
        <div style={{ position: "relative", marginTop: "20px" }}>
          <button
            style={{
              position: "absolute",
              top: "-1px",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 1,
              width: "60px",
              height: "10px",
              backgroundColor: "white",
              border: "none",
            }}
            onClick={() => {
              setIsExpanded(!isExpanded);
              setIsVisible(!isVisible);
            }}
          >
            <p style={{ fontSize: "20px", color: "black" }}>...</p>
          </button>

          {/* <TableTestReportCard
            headers={headers}
            data={tableData}
            searchPlaceholder="Search by Student Name..."
            isColumnDivVisible={isColumnDivVisible}
            setIsColumnDivVisible={setIsColumnDivVisible}
            dataClient={dataClient}
            testname={[]}
            ReportPageHeader="Student Progress Trend"
            course={courseName}
            batch={batchName}
            test=""
            date=""
            isExpanded={isExpanded}
          /> */}
          <TableTestReportCard
            headers={headers}
            data={tableData}
            searchPlaceholder="Search by Student Name..."
            isColumnDivVisible={isColumnDivVisible}
            setIsColumnDivVisible={setIsColumnDivVisible}
            dataClient={dataClient}
            testname={[]}
            ReportPageHeader="Student Progress Trend"
            course={courseName}
            batch={batchName}
            test=""
            date=""
            isExpanded={isExpanded}
            showAllColumns={true}
          />
        </div>
      </div>
    </div>
  );
};

export default StudentProgressTrend;
